/**
 * Decorative dashboard graphic for the "مشاهده دمو" card on `StartPage` —
 * a filled-in business-overview panel (real-looking KPI numbers, a
 * colored list and a bar chart) paired with `BuildWorkspaceIllustration`'s
 * empty version, so the two cards read as the same panel in two states.
 * Purely presentational — no real data, no claimed screenshot of the
 * product. Colors come from the `--sk-*` v2 design tokens so it stays
 * correct in both themes without its own palette.
 */
export function DemoDashboardIllustration() {
  return (
    <svg
      viewBox="0 0 380 236"
      width="100%"
      height="220"
      role="img"
      aria-label="پیش‌نمایش داشبورد نمونه با داده‌های واقعی"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="sk-demo-illu-back" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--sk-brand-700)" />
          <stop offset="1" stopColor="var(--sk-brand-900)" />
        </linearGradient>
        <linearGradient id="sk-demo-illu-bar" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="var(--sk-brand-500)" />
          <stop offset="1" stopColor="var(--sk-brand-400)" />
        </linearGradient>
        <filter id="sk-demo-illu-shadow-front" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy="14"
            stdDeviation="16"
            floodColor="var(--sk-brand-900)"
            floodOpacity="0.2"
          />
        </filter>
        <filter id="sk-demo-illu-shadow-back" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy="10"
            stdDeviation="12"
            floodColor="var(--sk-fg)"
            floodOpacity="0.16"
          />
        </filter>
      </defs>

      <g filter="url(#sk-demo-illu-shadow-back)">
        <rect x="228" y="10" width="126" height="196" rx="22" fill="url(#sk-demo-illu-back)" />
        <rect x="250" y="30" width="46" height="30" rx="8" fill="var(--sk-brand-400)" />
        <rect x="250" y="72" width="82" height="8" rx="4" fill="rgba(255,255,255,0.22)" />
        <rect x="250" y="88" width="60" height="8" rx="4" fill="rgba(255,255,255,0.16)" />
        <rect x="250" y="104" width="70" height="8" rx="4" fill="rgba(255,255,255,0.16)" />
      </g>

      <g filter="url(#sk-demo-illu-shadow-front)">
        <rect
          x="4"
          y="32"
          width="300"
          height="196"
          rx="20"
          fill="var(--sk-bg-elev)"
          stroke="var(--sk-border)"
        />
        <circle cx="24" cy="52" r="4" fill="var(--sk-danger)" />
        <circle cx="38" cy="52" r="4" fill="var(--sk-warn)" />
        <circle cx="52" cy="52" r="4" fill="var(--sk-success)" />
        <line x1="24" y1="66" x2="284" y2="66" stroke="var(--sk-divider)" />

        <rect x="24" y="80" width="78" height="36" rx="10" fill="var(--sk-bg-canvas)" />
        <text
          x="63"
          y="103"
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="var(--sk-fg)"
          className="sk-num"
        >
          ۴۲
        </text>
        <rect x="112" y="80" width="86" height="36" rx="10" fill="var(--sk-bg-canvas)" />
        <text
          x="155"
          y="103"
          textAnchor="middle"
          fontSize="13"
          fontWeight="700"
          fill="var(--sk-fg)"
          className="sk-num"
        >
          ۱.۸ میلیارد
        </text>
        <rect x="208" y="80" width="76" height="36" rx="10" fill="var(--sk-bg-canvas)" />
        <text
          x="246"
          y="103"
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="var(--sk-fg)"
          className="sk-num"
        >
          ۳۴۸
        </text>

        <rect x="24" y="128" width="118" height="86" rx="12" fill="var(--sk-bg-sunken)" />
        <circle cx="40" cy="144" r="4" fill="var(--sk-brand-500)" />
        <rect x="50" y="141" width="72" height="7" rx="3.5" fill="var(--sk-border-strong)" />
        <circle cx="40" cy="160" r="4" fill="var(--sk-success)" />
        <rect x="50" y="157" width="56" height="7" rx="3.5" fill="var(--sk-border-strong)" />
        <circle cx="40" cy="176" r="4" fill="var(--sk-warn)" />
        <rect x="50" y="173" width="64" height="7" rx="3.5" fill="var(--sk-border-strong)" />
        <circle cx="40" cy="192" r="4" fill="var(--sk-chart-5)" />
        <rect x="50" y="189" width="48" height="7" rx="3.5" fill="var(--sk-border-strong)" />

        <rect x="150" y="128" width="134" height="86" rx="12" fill="var(--sk-bg-sunken)" />
        <rect x="166" y="182" width="14" height="20" rx="4" fill="url(#sk-demo-illu-bar)" />
        <rect x="188" y="166" width="14" height="36" rx="4" fill="url(#sk-demo-illu-bar)" />
        <rect x="210" y="150" width="14" height="52" rx="4" fill="url(#sk-demo-illu-bar)" />
        <rect x="232" y="172" width="14" height="30" rx="4" fill="url(#sk-demo-illu-bar)" />
        <rect x="254" y="142" width="14" height="60" rx="4" fill="url(#sk-demo-illu-bar)" />
      </g>
    </svg>
  );
}
